/**
 * La coquille de l'espace tuteur : ce que son en-tête et sa feuille de profil montrent.
 *
 * Port de `TutorMainMenuHeader.js` (l'avatar, le point quand l'e-mail n'est pas vérifié), de
 * `ProfileInfo.js` (« Membre depuis … », le fuseau quand il diffère de celui du serveur) et des portes
 * de l'ancien « Mon compte » (`MyAccountScreen.js` : Ma dispo et Mes factures avec le droit
 * `my_students` ; `StripeExpressDashboardButton.js` : Suivre mes paiements et Historique des lives,
 * compte Stripe rempli).
 *
 * Mémoïsé sur les scalaires qu'il rend (`ports/CLAUDE.md` § view-models) : le compte change
 * d'identité à chaque relecture, pas son prénom. Le droit `my_students` vient du contexte des
 * droits, que l'écran lit et passe (`canSeeStudents`).
 */
import moment from "moment"
import { createSelector } from "reselect"

import { type UITutorAccountLinkKey, type UITutorMe } from "../../../ui/types/tutorCoquilleAccueil"
import { selectAccount } from "../../shared/displayedStudent"

type Account = {
    firstname?: string | null
    display_name?: string | null
    avatar?: string | null
    is_verified?: boolean | number | null
    created_at?: string | null
    timezone?: string | null
    stripe_connect_details_submitted?: boolean | number | null
} | null

type ShellState = { data?: { staticData?: { server_timezone?: string | null } } }

export type TutorShellViewModel = {
    me: UITutorMe
    /** Les portes de son compte que ses droits ouvrent, dans l'ordre de l'ancienne page. */
    accountLinks: UITutorAccountLinkKey[]
}

const account = (state: unknown) => selectAccount(state as never) as Account

const selectShell = createSelector(
    [
        (state: unknown) => account(state)?.firstname ?? "",
        (state: unknown) => account(state)?.display_name ?? "",
        (state: unknown) => account(state)?.avatar ?? undefined,
        (state: unknown) => account(state)?.is_verified,
        (state: unknown) => account(state)?.created_at ?? null,
        (state: unknown) => account(state)?.timezone ?? null,
        (state: unknown) => (state as ShellState)?.data?.staticData?.server_timezone ?? null,
        (state: unknown) => !!account(state)?.stripe_connect_details_submitted,
        (_state: unknown, canSeeStudents: boolean) => canSeeStudents,
    ],
    (
        firstname,
        displayName,
        avatarUri,
        isVerified,
        createdAt,
        timezone,
        serverTimezone,
        stripeConnected,
        canSeeStudents
    ): TutorShellViewModel => ({
        me: {
            firstname,
            displayName: displayName || firstname,
            avatarUri: avatarUri || undefined,
            // L'ancien en-tête comptait « 1 » sur l'avatar tant que l'e-mail n'était pas vérifié.
            alert: !isVerified,
            memberSince: createdAt ? moment(createdAt).format("MMM YYYY") : undefined,
            // `ProfileInfo` : le fuseau ne s'écrit que s'il diffère de celui du serveur.
            timezone: timezone && serverTimezone && timezone !== serverTimezone ? timezone : undefined,
        },
        accountLinks: [
            ...(canSeeStudents ? (["availability", "invoices"] as const) : []),
            ...(stripeConnected ? (["payments", "livesHistory"] as const) : []),
        ],
    })
)

export function tutorShellViewModel(state: unknown, canSeeStudents: boolean): TutorShellViewModel {
    return selectShell(state, canSeeStudents)
}
